import { useState } from 'react';
import Img from '@/components/base/Img';
import SectionLabel from '@/components/feature/SectionLabel';
import { materials } from '@/mocks/site';

const props = ['Matte, clouded, translucent', 'Fine aggregate, float finish', 'Hand relief, deep shadow', 'Smooth matte, weather-facing'];

export default function SurfaceLibrary() {
  // Order of material indexes: first = large featured image, rest = right-hand list.
  const [order, setOrder] = useState<number[]>(() => materials.map((_, i) => i));
  const [featuredIdx, ...restIdx] = order;
  const featured = materials[featuredIdx];

  const swap = (idx: number) => {
    setOrder((o) => o.map((v) => {
      if (v === idx) return o[0];
      if (v === o[0]) return idx;
      return v;
    }));
  };

  return (
    <section className="py-20 md:py-28">
      <div className="shell">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-6 mb-12 md:mb-16 items-end">
          <div className="md:col-span-3"><SectionLabel index="03">Surface Library</SectionLabel></div>
          <h2 data-reveal="title" className="md:col-span-5 md:col-start-4 h-section">Four materials, four ways of holding light</h2>
          <p className="md:col-span-3 md:col-start-10 text-sm leading-relaxed text-foreground-700">
            Suitability depends on the existing substrate, which we confirm on site.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-12 items-start">
          <article className="lg:col-span-7">
            {/* All four images stay mounted and stacked; switching only crossfades opacity (no remount, no reload). */}
            <div className="relative w-full aspect-[5/6] lg:aspect-[7/8] overflow-hidden">
              {materials.map((m, i) => (
                <div
                  key={m.name}
                  aria-hidden={i !== featuredIdx}
                  className={`absolute inset-0 transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
                    i === featuredIdx ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-[1.03] z-0'
                  }`}
                >
                  <Img src={m.image} alt={`${m.name} exterior wall texture`} className="w-full h-full" eager />
                </div>
              ))}
            </div>
            <div key={featuredIdx} className="surface-fade mt-4 grid grid-cols-[2.5rem_1fr] gap-3 items-baseline">
              <span className="meta text-accent-600">M.0{featuredIdx + 1}</span>
              <div>
                <h3 className="font-heading font-medium text-lg">{featured.name}</h3>
                <p className="meta mt-1">{props[featuredIdx]}</p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700 max-w-md">{featured.note}</p>
              </div>
            </div>
          </article>

          <div className="lg:col-span-5 lg:sticky lg:top-20 flex flex-col border-t border-foreground-950">
            {restIdx.map((idx) => {
              const m = materials[idx];
              return (
                <button
                  type="button"
                  key={m.name}
                  onClick={() => swap(idx)}
                  aria-label={`Show ${m.name} as main image`}
                  className="group text-left grid grid-cols-[96px_1fr] sm:grid-cols-[150px_1fr] gap-5 py-6 border-b border-background-300 hover:border-foreground-950 transition-colors cursor-pointer"
                >
                  <div className="overflow-hidden">
                    <Img src={m.image} alt={`${m.name} exterior wall texture`} className="w-full aspect-square transition-transform duration-500 group-hover:scale-[1.04]" eager />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-heading font-medium text-base group-hover:text-accent-600 transition-colors">{m.name}</h3>
                      <span className="meta text-accent-600">M.0{idx + 1}</span>
                    </div>
                    <p className="meta mt-1">{props[idx]}</p>
                    <p className="mt-3 text-[13px] leading-relaxed text-foreground-700">{m.note}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}